/**
 * Main Agent (Orchestrator)
 *
 * A single LLM.streamWithTools() call that handles everything:
 * - Routing: decides which source(s) to query based on summaries
 * - Querying: calls source tools (each wraps an independent SourceAgent)
 * - Direct tools: calls pre-built function tools directly with LLM-provided params
 * - Re-querying: if data is wrong/incomplete, calls tools again with modified intent
 * - Analysis: generates final text response from the data
 *
 * Two tool types:
 * - "source" tools: main agent sees summaries, SourceAgent handles SQL generation independently
 * - "direct" tools: main agent calls fn() directly with structured params (no SourceAgent)
 */

import { LLM, RequestAbortedError } from '../../llm';
import { promptLoader } from '../prompt-loader';
import { getCurrentDateTimeForPrompt } from '../../utils/datetime';
import { logger } from '../../utils/logger';
import { extractPromptText } from '../prompt-extractor';
import { StreamBuffer, TaggedStreamBuffer, streamDelay } from '../stream-buffer';
import { formatToolResultForLLM } from '../llm-result-truncator';
import { safeTruncate } from '../../utils/surrogate';
import { summarizeRows, MAIN_AGENT_COMPLETE_ROWS } from './data-summary';
import type { ExternalTool, ExecutedToolInfo } from '../services/tool-executor-service';
import type {
	SourceSummary,
	SourceAgentResult,
	AgentResponse,
	AgentConfig,
	SourceToolInput,
	AgentWrittenScript,
	WorkflowDescriptor,
	SelectedWorkflow,
} from './types';
import { buildSourceSummaries, formatSummariesForPrompt } from './agent-prompt-builder';
import { SourceAgent } from './source-agent';
import { runScript } from '../scripts/script-runner';
import { ScriptStore } from '../scripts/script-store';
import type { ScriptParameter, ScriptResult } from '../scripts/types';
import { TOOL_TRACKING_SAMPLE_ROWS, MAX_ROWS_RENDERED, MAX_ROWS_FETCHED } from '../constants';

// ============================================
// Script-writing tool schemas (exposed to the main-agent LLM)
// ============================================

const WRITE_SCRIPT_TOOL_DEF = {
	name: 'write_script',
	description:
		'Save a draft script that reproduces the answer to the user question. You MUST call this after every successful source query and BEFORE writing your final analysis — the final answer must come from the script\'s output, not from the source tool result directly. Even passthrough scripts (just ctx.query + return) are correct and required: they get saved for future matching. ' +
		'The scriptBody must start with `export async function getData(ctx, params)` — it runs in a tsx subprocess with ctx.query (authorized queries), ctx.emit (register computed datasets), ctx.log (stream progress), and ctx.now. ' +
		'Any query SQL used here MUST match a SQL that already succeeded via a source tool in this turn.',
	input_schema: {
		type: 'object',
		properties: {
			name: { type: 'string', description: 'Short, descriptive title (3–5 words).' },
			description: { type: 'string', description: 'One-sentence description of what this script retrieves.' },
			tags: { type: 'array', items: { type: 'string' }, description: '5–8 keywords for future matching.' },
			scriptBody: { type: 'string', description: 'Full TS script body starting with `export async function getData(ctx, params)`. No imports needed for ctx.query / ctx.emit / ctx.now. CRITICAL: write every SQL string (and any multi-line string) as a backtick template literal, NEVER with single or double quotes — multi-line SQL inside quotes breaks at the line break and causes an "Unterminated string literal" compile error. Keep string interpolation inside the backticks (${params.x}).' },
			parameters: {
				type: 'array',
				description: 'Declared parameters the script consumes.',
				items: {
					type: 'object',
					properties: {
						name: { type: 'string' },
						type: { type: 'string', enum: ['string', 'number', 'date', 'date_range', 'enum', 'boolean'] },
						required: { type: 'boolean' },
						default: {},
						description: { type: 'string' },
					},
					required: ['name', 'type', 'description'],
				},
			},
		},
		required: ['name', 'description', 'scriptBody'],
	},
};

const EXECUTE_SCRIPT_TOOL_DEF = {
	name: 'execute_script',
	description:
		'Run the current draft script in a subprocess and return { ok, sampleRows, executedQueries, error?, errorPhase? }. ' +
		'Call this after write_script. If ok is false, fix the draft with a fresh write_script (max 3 attempts per turn). ' +
		'If ok is true AND the sample rows answer the user question, proceed to your final analysis.',
	input_schema: {
		type: 'object',
		properties: {},
	},
};

const MAX_SCRIPT_ATTEMPTS = 3;

/**
 * Per-turn draft state. The script body itself lives on disk in
 * `scripts-store/<slug>-<turnId>.ts` from the moment `write_script` fires —
 * this state is just the bookkeeping needed to find it again on
 * `execute_script` and to surface the verified result to the caller.
 */
interface ScriptState {
	/** Recipe id of the on-disk draft. Stable across retries within the turn. */
	recipeId: string | null;
	attempts: number;
	lastSuccessfulResult: ScriptResult | null;
}

// ============================================
// Main Agent
// ============================================

export class MainAgent {
	private externalTools: ExternalTool[];
	private workflows: WorkflowDescriptor[];
	private config: AgentConfig;
	private streamBuffer: StreamBuffer;
	/**
	 * Optional: when provided, MainAgent exposes the `write_script` /
	 * `execute_script` tools to the LLM and persists drafts to disk via the
	 * store. Headless callers (alert analyzer, metric resolver) omit these to
	 * suppress script authoring entirely — drafts would otherwise leak onto
	 * disk with no caller to promote or clean them up.
	 */
	private scriptStore: ScriptStore | null;
	private turnId: string | null;
	private createdFromPrompt: string = '';
	private scriptState: ScriptState = { recipeId: null, attempts: 0, lastSuccessfulResult: null };
	/**
	 * Fork mode — set when this turn is adapting a near-matching parent script.
	 * In fork mode there is no legitimate "answer with bare text" outcome: the
	 * only correct first move is a tool call (write_script, or a source tool for
	 * schema discovery). We therefore force tool use on the first LLM iteration
	 * so the model can't end its turn with a bare "I'll adapt…" preamble and zero
	 * tool calls. Never set on the fresh-authoring / general-question path.
	 */
	private forkMode: boolean;
	/**
	 * Per-turn cancellation signal (user hit "Stop"). Set at the top of
	 * handleQuestion and read by the tool handler, the SourceAgent dispatch, and
	 * the script subprocess so an abort tears down every layer of the turn.
	 */
	private abortSignal?: AbortSignal;

	constructor(
		externalTools: ExternalTool[],
		config: AgentConfig,
		scriptStore?: ScriptStore,
		turnId?: string,
		streamBuffer?: StreamBuffer,
		workflows: WorkflowDescriptor[] = [],
		forkMode: boolean = false,
	) {
		this.externalTools = externalTools;
		this.workflows = workflows;
		this.config = config;
		this.streamBuffer = streamBuffer || new StreamBuffer();
		this.scriptStore = scriptStore ?? null;
		this.turnId = turnId ?? null;
		this.forkMode = forkMode;
	}

	private get scriptingEnabled(): boolean {
		return this.scriptStore !== null && this.turnId !== null;
	}

	/**
	 * Handle a user question using the multi-agent system.
	 *
	 * This is ONE LLM.streamWithTools() call. The LLM:
	 * 1. Sees source summaries + direct tool descriptions in system prompt
	 * 2. Decides which tool(s) to call (routing)
	 * 3. Source tools → SourceAgent runs independently → returns data
	 * 4. Direct tools → fn() called directly with LLM params → returns data
	 * 5. Generates final analysis text
	 */
	async handleQuestion(
		userPrompt: string,
		apiKey?: string,
		conversationHistory?: string,
		streamCallback?: (chunk: string) => void,
		signal?: AbortSignal,
	): Promise<AgentResponse> {
		const startTime = Date.now();
		logger.info(`[MainAgent] Starting | prompt: "${userPrompt.substring(0, 50)}..."`);

		// Reset per-turn script state so a new question starts fresh
		this.scriptState = { recipeId: null, attempts: 0, lastSuccessfulResult: null };
		this.createdFromPrompt = userPrompt;
		this.abortSignal = signal;

		// Separate source tools from direct tools
		const sourceTools = this.externalTools.filter(t => (t as any).toolType !== 'direct');
		const directTools = this.externalTools.filter(t => (t as any).toolType === 'direct');

		logger.info(`[MainAgent] ${sourceTools.length} source tool(s), ${directTools.length} direct tool(s), ${this.workflows.length} workflow(s)`);

		// Build source summaries for the system prompt
		const summaries = buildSourceSummaries(sourceTools);

		// Build the system prompt with summaries + direct tool descriptions + workflow descriptions
		const systemPrompt = await this.buildSystemPrompt(summaries, directTools, this.workflows, conversationHistory);

		logger.logLLMPrompt('mainAgent', 'system', extractPromptText(systemPrompt));
		logger.logLLMPrompt('mainAgent', 'user', userPrompt);

		// Build LLM tool definitions — source tools + direct tools + workflow tools + script-authoring tools
		const sourceToolDefs = this.buildSourceToolDefinitions(summaries);
		const directToolDefs = this.buildDirectToolDefinitions(directTools);
		const workflowToolDefs = this.buildWorkflowToolDefinitions(this.workflows);
		const tools = [
			...sourceToolDefs,
			...directToolDefs,
			...workflowToolDefs,
			...(this.scriptingEnabled ? [WRITE_SCRIPT_TOOL_DEF, EXECUTE_SCRIPT_TOOL_DEF] : []),
		];

		// Track all results for component generation
		const sourceResults: SourceAgentResult[] = [];
		const executedTools: ExecutedToolInfo[] = [];
		let sourceCallCounter = 0;
		// Captured when the LLM picks a workflow tool. Short-circuits the response
		// downstream — we skip analysis text and component generation.
		let selectedWorkflow: SelectedWorkflow | undefined;

		// Tool handler — routes to SourceAgent, direct execution, workflow capture, or script-authoring
		const toolHandler = async (toolName: string, toolInput: any): Promise<string> => {
			// Workflow lookup first — workflows have priority over tools with the same id
			const workflow = this.workflows.find(w => w.id === toolName);
			if (workflow) {
				return this.handleWorkflow(workflow, toolInput, w => { selectedWorkflow = w; });
			}
			if (toolName === 'write_script') {
				if (!this.scriptingEnabled) return 'Scripting is not enabled for this request.';
				return this.handleWriteScript(toolInput);
			}
			if (toolName === 'execute_script') {
				if (!this.scriptingEnabled) return 'Scripting is not enabled for this request.';
				return this.handleExecuteScript();
			}

			const externalTool = this.externalTools.find(t => t.id === toolName);
			if (!externalTool) {
				logger.error(`[MainAgent] Unknown tool called: ${toolName}`);
				return `Error: Unknown tool "${toolName}"`;
			}

			// Direct tool — call fn() directly with LLM-provided params
			if ((externalTool as any).toolType === 'direct') {
				return this.handleDirectTool(externalTool, toolInput, executedTools);
			}

			// Source tool — dispatch to independent SourceAgent
			const sourceInput: SourceToolInput = {
				intent: toolInput.intent || toolInput.query || JSON.stringify(toolInput),
				aggregation: toolInput.aggregation || 'raw',
			};

			logger.info(`[MainAgent] Dispatching SourceAgent for "${externalTool.name}" | intent: "${sourceInput.intent}"`);

			// Pre-resolve schema via embedding search when available.
			// Large/very_large sources benefit the most — avoids LLM-driven search_schema iterations.
			const preResolved = await this.preResolveSchema(externalTool, sourceInput.intent);

			// Create a tagged stream buffer for this specific source agent call.
			// Each call gets a unique ID so the frontend can group interleaved
			// messages from parallel source agents into separate blocks.
			sourceCallCounter++;
			const callId = `src_${sourceCallCounter}`;
			const taggedBuffer = new TaggedStreamBuffer(
				callId,
				this.streamBuffer,
				externalTool.name,
				sourceInput.intent
			);

			const sourceAgent = new SourceAgent(
				externalTool,
				this.config,
				taggedBuffer,
				// Pre-resolved tables are a HINT, not a lock: pass them as the schema
				// starting point but KEEP search_schema available so the agent can
				// recover within the dispatch when the embedding picked wrong tables —
				// instead of forcing the MainAgent into a re-dispatch loop.
				{
					...(preResolved ? { preResolvedSchema: preResolved } : {}),
					signal: this.abortSignal,
				},
			);
			const result = await sourceAgent.execute(sourceInput);

			sourceResults.push(result);
			if (result.success) {
				// Push ALL successful queries (primary + follow-up) so component generator
				// has the SQL and sample data from every query, not just the last one.
				if (result.allExecutedTools && result.allExecutedTools.length > 0) {
					executedTools.push(...result.allExecutedTools);
				} else {
					executedTools.push(result.executedTool);
				}
			}

			return this.formatResultForMainAgent(result);
		};

		// ONE LLM.streamWithTools() call — the LLM handles everything
		let text = '';
		try {
			text = await LLM.streamWithTools(
				{
					sys: systemPrompt,
					user: userPrompt,
				},
				tools,
				toolHandler,
				{
					model: this.config.mainAgentModel || undefined,
					// write_script emits a full script body + parameters JSON in a single
					// tool call; 4000 truncated forked bodies (stop_reason=max_tokens),
					// which dropped the tool call entirely. 8000 gives the body room.
					maxTokens: 8000,
					temperature: 0,
					apiKey: apiKey || this.config.apiKey,
					partial: streamCallback,
					signal,
					// Fork mode: force a tool call on iteration 1 so the model can't
					// stop at a bare "I'll adapt…" preamble. `any` lets it pick
					// write_script (common) or a source tool (case-A schema discovery).
					// Only when scripting is enabled — the tools must actually exist.
					...(this.forkMode && this.scriptingEnabled
						? { firstIterationToolChoice: { type: 'any' as const } }
						: {}),
				},
				this.config.maxIterations
			);
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			// User cancelled — propagate the abort untouched. Do NOT recover with a
			// partial script result: the user wants to stop and re-ask, not receive
			// a half-finished answer.
			if (signal?.aborted || err instanceof RequestAbortedError) {
				throw err;
			}
			// Safety net: the tool loop can throw (e.g. "Max iterations reached")
			// AFTER a script already executed successfully — common on weaker models
			// that over-investigate instead of finalizing. Don't discard a verified
			// result: if a script succeeded this turn, recover and finalize with it
			// rather than failing the whole request. Only re-throw when there is
			// nothing usable to fall back to.
			if (this.scriptState.lastSuccessfulResult) {
				logger.warn(`[MainAgent] Tool loop ended early (${msg}) but a script already executed successfully — recovering with the last verified result instead of failing.`);
				text = '';
			} else {
				throw err;
			}
		}

		const totalTime = Date.now() - startTime;
		logger.info(
			`[MainAgent] Complete | ${sourceResults.length} source queries, ${executedTools.length} successful${selectedWorkflow ? ` | workflow="${selectedWorkflow.name}"` : ''} | ${totalTime}ms`,
		);

		const savedScript = await this.buildSavedScript();
		if (savedScript) {
			logger.info(`[MainAgent] Script authored: "${savedScript.name}" (${savedScript.parameters.length} params, ${this.scriptState.attempts} attempt${this.scriptState.attempts === 1 ? '' : 's'})`);
		} else if (sourceResults.some(r => r.success)) {
			logger.warn(`[MainAgent] Source query succeeded but no script was authored — LLM skipped write_script/execute_script. Prompt policy may need tightening.`);
		}

		// Immediate cleanup of a failed draft (#5). If the agent authored a draft
		// this turn but it never verified (no savedScript → never promoted), discard
		// it now — the row + on-disk body. Failed drafts are never matched
		// (status='verified' only), so nothing of value is lost, and we avoid
		// unbounded draft accumulation + squatted file_base slug slots. Successful
		// drafts are promoted to verified by the caller, so we never touch those.
		// NOTE: this runs once at end-of-turn, NOT on each execute_script failure —
		// intra-turn retries reuse the same draft, so deleting mid-loop would break them.
		if (!savedScript && this.scriptStore && this.scriptState.recipeId) {
			try {
				await this.scriptStore.discardDraft(this.scriptState.recipeId);
			} catch (err) {
				logger.warn(`[MainAgent] Failed to discard failed draft ${this.scriptState.recipeId}: ${err}`);
			}
		}

		return {
			text,
			executedTools,
			sourceResults,
			savedScript,
			workflow: selectedWorkflow,
		};
	}

	// ============================================
	// Script-authoring tool handlers
	// ============================================

	private async handleWriteScript(toolInput: any): Promise<string> {
		const scriptBody = typeof toolInput?.scriptBody === 'string' ? toolInput.scriptBody.trim() : '';
		if (!scriptBody) {
			return 'write_script requires a non-empty `scriptBody` starting with `export async function getData(ctx, params)`.';
		}
		const hasGetDataExport =
			/\bexport\s+(async\s+)?function\s+getData\b/.test(scriptBody) ||
			/\bexport\s+default\s+(async\s+)?function\b/.test(scriptBody);
		if (!hasGetDataExport) {
			return "write_script rejected: scriptBody does not export `getData`. The script MUST contain `export async function getData(ctx, params) { ... }` as a named export — the subprocess runner imports this function by name. Your current scriptBody is missing the `export` keyword or uses a non-function export (const, class, module.exports). Rewrite the body with exactly: `export async function getData(ctx, params) { ... }`.";
		}

		const name = toolInput.name || 'Untitled Script';
		const intentDescription = toolInput.description || '';
		const tags = Array.isArray(toolInput.tags) ? toolInput.tags : [];
		const parameters = Array.isArray(toolInput.parameters)
			? this.normalizeParameterList(toolInput.parameters)
			: [];

		// Persist the draft to disk immediately so it's visible in the IDE.
		// On retries within the same turn we pass the existing recipeId so the
		// store overwrites the same file rather than minting a new one.
		// scriptingEnabled is checked upstream — store/turnId are non-null here.
		const draft = await this.scriptStore!.saveDraft({
			recipeId: this.scriptState.recipeId ?? undefined,
			turnId: this.turnId!,
			name,
			intentDescription,
			tags,
			parameters,
			scriptBody,
			createdFrom: this.createdFromPrompt,
		});
		this.scriptState.recipeId = draft.id;
		// A new draft invalidates any previous successful run — it must be re-executed.
		this.scriptState.lastSuccessfulResult = null;

		logger.info(
			`[MainAgent] write_script: draft "${draft.name}" persisted to disk (${draft.parameters.length} params, recipeId=${draft.id})`,
		);
		return `Draft "${draft.name}" saved to disk. Call execute_script to verify it works.`;
	}

	private async handleExecuteScript(): Promise<string> {
		if (!this.scriptState.recipeId) {
			return 'No draft found. Call write_script first with the script you want to verify.';
		}

		const draftRecipe = await this.scriptStore!.get(this.scriptState.recipeId);
		if (!draftRecipe) {
			logger.error(`[MainAgent] execute_script: draft "${this.scriptState.recipeId}" missing from store`);
			return 'Draft was lost from the store. Call write_script again.';
		}

		if (this.scriptState.attempts >= MAX_SCRIPT_ATTEMPTS) {
			return `Reached the max of ${MAX_SCRIPT_ATTEMPTS} execute_script attempts for this turn. Stop calling execute_script and answer the user directly from the source data you already have.`;
		}
		this.scriptState.attempts++;

		// Subprocess reads the on-disk draft path directly — no temp file dance.
		const scriptPath = this.scriptStore!.getScriptPath(draftRecipe);

		const result: ScriptResult = await runScript(
			draftRecipe,
			scriptPath,
			{},
			{ externalTools: this.externalTools, streamBuffer: this.streamBuffer, signal: this.abortSignal },
		);

		if (result.success) {
			this.scriptState.lastSuccessfulResult = result;
			const totalRows = result.data.length;
			// Explicit full-vs-truncated signal: a query is truncated when the source
			// reported MORE matching rows (totalCount) than it returned (count). If any
			// query was truncated, the summary/extremes cover only the returned rows —
			// the agent must NOT present totals as final and should re-aggregate.
			const limited = result.executedQueries.filter(
				q => typeof q.totalCount === 'number' && q.totalCount > q.count,
			);
			const dataComplete = limited.length === 0;
			const completenessNote = dataComplete
				? `COMPLETENESS: this IS the FULL result — all ${totalRows} rows are present, nothing truncated. ` +
					`dataSummary/extremes/topK/ordering are computed over the COMPLETE set, so treat every ` +
					`total, min/max, and ranking as final.`
				: `⚠️ COMPLETENESS: this result is TRUNCATED — ` +
					`${limited.map(q => `"${q.sourceName}" returned ${q.count} of ${q.totalCount} matching rows`).join('; ')}. ` +
					`dataSummary/extremes/topK cover ONLY the returned rows, NOT the full match — do NOT present any ` +
					`total, min/max, or ranking as final; re-author the query to AGGREGATE (GROUP BY) so the metric is ` +
					`computed in SQL over ALL rows.`;
			const summary = {
				ok: true,
				executionTimeMs: result.executionTimeMs,
				totalRows,
				// Explicit boolean the agent (and downstream component gen) can branch on.
				dataComplete,
				// dataSummary is computed over ALL returned rows — use it for any range/
				// count/min/max/total/"all-or-none" claim. sampleRows is just the first few
				// rows (head of result, NOT representative) for illustrating shape.
				dataSummary: summarizeRows(result.data),
				sampleRows: result.data.slice(0, 5),
				analysisGuidance:
					`${completenessNote} ` +
					`Showing ${Math.min(totalRows, 5)} of ${totalRows} rows. The sample is the ` +
					`HEAD of the result and is NOT representative — do not generalize from it. ` +
					`Base every number, range, count, and "all/none" statement on dataSummary, ` +
					`which is computed over all ${totalRows} returned rows. Name any highest/lowest/peak/` +
					`trough via dataSummary.extremes and dataSummary.ordering (they carry the LABEL ` +
					`row — never guess which row holds an extreme); state any "top/bottom N" from ` +
					`dataSummary.topK / dataSummary.bottomK. If dataSummary.ordering.lastPeriodPartial ` +
					`is true, call out that the latest period is incomplete. The full result is ` +
					`rendered for the user by the on-screen component.`,
				executedQueries: result.executedQueries.map(q => ({
					sourceId: q.sourceId,
					sourceName: q.sourceName,
					rows: q.count,
					sample: q.data.slice(0, 2),
				})),
			};
			logger.info(`[MainAgent] execute_script: ok — ${totalRows} rows in ${result.executionTimeMs}ms (attempt ${this.scriptState.attempts})`);
			return JSON.stringify(summary, null, 2);
		}

		// Stamp the error onto the draft metadata so users can read it without grepping logs.
		await this.scriptStore!.recordDraftError(draftRecipe.id, {
			phase: (result.errorPhase ?? 'runtime'),
			message: result.error ?? 'Unknown error',
			attempt: this.scriptState.attempts,
		});

		const attemptsRemaining = MAX_SCRIPT_ATTEMPTS - this.scriptState.attempts;
		const summary = {
			ok: false,
			errorPhase: result.errorPhase,
			error: result.error,
			attemptsRemaining,
			executedQueries: result.executedQueries.map(q => ({
				sourceId: q.sourceId,
				sourceName: q.sourceName,
				rows: q.count,
			})),
			hint:
				attemptsRemaining > 0
					? 'Call write_script with a corrected draft, then execute_script again. If you cannot fix it, stop and answer from the source data you already have.'
					: 'No attempts remaining — stop calling execute_script and answer the user directly.',
		};
		logger.warn(`[MainAgent] execute_script: failed [${result.errorPhase}] — ${result.error} (attempt ${this.scriptState.attempts}/${MAX_SCRIPT_ATTEMPTS})`);
		return JSON.stringify(summary, null, 2);
	}

	/**
	 * Build the AgentWrittenScript payload the caller will hand to
	 * `ScriptStore.promoteToVerified()`. Only returned when a verified
	 * successful execution is on record.
	 */
	private async buildSavedScript(): Promise<AgentWrittenScript | undefined> {
		const { recipeId, lastSuccessfulResult } = this.scriptState;
		if (!recipeId || !lastSuccessfulResult) return undefined;

		if (!this.scriptStore) return undefined;
		const draft = await this.scriptStore.get(recipeId);
		if (!draft) return undefined;

		const executedQueries = lastSuccessfulResult.executedQueries.map(q => ({
			sourceId: q.sourceId,
			sourceName: q.sourceName,
			sql: q.sql,
			data: q.data,
			count: q.count,
			totalCount: q.totalCount,
			executionTimeMs: q.executionTimeMs,
			virtual: q.virtual,
		}));
		const sourceIds = Array.from(new Set(executedQueries.map(q => q.sourceId).filter(id => !id.startsWith('computed:'))));
		const tables = extractTablesFromSQL(executedQueries.map(q => q.sql));

		return {
			recipeId: draft.id,
			name: draft.name,
			intentDescription: draft.intentDescription,
			tags: draft.tags,
			parameters: draft.parameters,
			scriptBody: draft.scriptBody,
			sourceIds,
			tables,
			executedQueries,
		};
	}

	private normalizeParameterList(input: any[]): ScriptParameter[] {
		return input
			.filter(p => p && typeof p === 'object' && typeof p.name === 'string')
			.map(p => ({
				name: p.name,
				type: (p.type || 'string') as ScriptParameter['type'],
				required: Boolean(p.required),
				default: p.default,
				enumValues: p.enumValues,
				description: typeof p.description === 'string' ? p.description : '',
			}));
	}

	// ============================================
	// Pre-Resolved Schema (embedding search)
	// ============================================

	/**
	 * Use the schema embedding collection to pre-select relevant tables for
	 * this source + intent. Returns a formatted schema block if confidence is
	 * high (top match ≥ 0.55 and ≥3 candidates), otherwise null.
	 *
	 * When this returns a block, we can skip the SourceAgent's `search_schema`
	 * loop and reduce iteration budget. When it returns null, the SourceAgent
	 * falls back to the existing LLM-driven keyword search (same as today).
	 */
	private async preResolveSchema(
		externalTool: ExternalTool,
		intent: string,
	): Promise<string | null> {
		const collections = this.config.collections;
		const search = collections?.['schema-embeddings']?.['search'];
		if (!search) return null;

		// Only worth doing for large/very_large tiers — small/medium already fit in prompt.
		const tier = (externalTool as any).schemaTier;
		if (tier !== 'large' && tier !== 'very_large') return null;

		try {
			// Tool ID has a `_query` / `_read` / `_call` suffix — strip it to get the real source ID,
			// which is the field schema embeddings are stored under.
			const sourceId = externalTool.id.replace(/_(query|read|call)$/, '');
			const res = await search({
				query: intent,
				sourceId,
				projectId: this.config.projectId,
				// Wider net for large ERP schemas: the right table (e.g. a join/bridge
				// table) often ranks 10–30, not top-10. Cheaper to hand the agent more
				// candidate tables once than to re-dispatch when the top-10 missed it.
				topK: 30,
			});
			const results = res?.results || [];
			if (!Array.isArray(results) || results.length === 0) {
				logger.info(`[MainAgent] Schema embedding returned 0 results for ${externalTool.name} (sourceId=${sourceId}) — falling back to LLM search_schema`);
				return null;
			}

			const top = results[0];
			// Similarity = 1/(1+L2distance) — typical MiniLM range 0.3-0.7 for relevant matches.
			// 0.4 is a loose bar that still filters irrelevant domains.
			if (!top || top.similarity < 0.4) {
				logger.info(`[MainAgent] Schema embedding low confidence (top=${top?.similarity?.toFixed(2)}) — falling back to LLM search_schema`);
				return null;
			}
			const highConfidence = results.filter((r: any) => r.similarity >= 0.35);
			if (highConfidence.length < 3) {
				logger.info(`[MainAgent] Schema embedding too few relevant tables (${highConfidence.length} >= 0.35) — falling back to LLM search_schema`);
				return null;
			}

			const lines = highConfidence.slice(0, 30).map((r: any) =>
				`• ${r.description} [similarity: ${r.similarity.toFixed(2)}]`
			);
			const header = `[Pre-resolved via embedding search — ${highConfidence.length} most relevant tables for this intent.]`;
			logger.info(`[MainAgent] Schema embedding pre-resolved ${highConfidence.length} tables for ${externalTool.name} (top=${top.similarity.toFixed(2)})`);
			return `${header}\n${lines.join('\n')}`;
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			logger.warn(`[MainAgent] Schema embedding search failed (non-blocking): ${msg}`);
			return null;
		}
	}

	// ============================================
	// Direct Tool Execution
	// ============================================

	/**
	 * Execute a direct tool — call fn() with LLM-provided params, no SourceAgent.
	 */
	private async handleDirectTool(
		tool: ExternalTool,
		toolInput: any,
		executedTools: ExecutedToolInfo[]
	): Promise<string> {
		const startTime = Date.now();
		logger.info(`[MainAgent] Executing direct tool "${tool.name}" | params: ${JSON.stringify(toolInput).substring(0, 200)}`);

		if (this.streamBuffer.hasCallback()) {
			this.streamBuffer.write(`\n\n🔧 **Running ${tool.name}...**\n\n`);
			await streamDelay();
		}

		try {
			const result = await tool.fn(toolInput);
			const executionTimeMs = Date.now() - startTime;

			// Check for error in result
			if (result && result.error) {
				const errorMsg = typeof result.error === 'string' ? result.error : JSON.stringify(result.error);
				logger.warn(`[MainAgent] Direct tool "${tool.name}" returned error: ${errorMsg}`);
				return `❌ Tool "${tool.name}" error: ${errorMsg}`;
			}

			const resultData = result.data || result;
			const rowCount = Array.isArray(resultData) ? resultData.length : 1;

			if (this.streamBuffer.hasCallback()) {
				this.streamBuffer.write(`✅ **${tool.name}** completed (${rowCount} results, ${executionTimeMs}ms)\n\n`);
			}

			// Track for component generation
			const formattedResult = formatToolResultForLLM(result, {
				toolName: tool.name,
				maxRows: 5,
				maxCharsPerField: 200,
			});

			// _sampleData feeds the component-generator prompt. Use the already-truncated
			// formattedResult.data so a tool returning a non-array object with large nested
			// arrays cannot blow the prompt window. Falls back to slicing the raw resultData
			// only when it is itself an array.
			const sampleData: unknown[] = Array.isArray(resultData)
				? resultData.slice(0, TOOL_TRACKING_SAMPLE_ROWS)
				: (Array.isArray(formattedResult.data) ? formattedResult.data.slice(0, TOOL_TRACKING_SAMPLE_ROWS) : []);

			executedTools.push({
				id: tool.id,
				name: tool.name,
				params: toolInput,
				result: {
					_totalRecords: result.totalItems || result.count || rowCount,
					_recordsShown: rowCount,
					_metadata: result.metadata,
					_sampleData: sampleData,
				},
				outputSchema: tool.outputSchema,
			});

			const formatted = typeof formattedResult === 'string' ? formattedResult : JSON.stringify(formattedResult);
			return `✅ Tool "${tool.name}" executed successfully. ${rowCount} results returned.\n\n${formatted}`;

		} catch (error) {
			const executionTimeMs = Date.now() - startTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[MainAgent] Direct tool "${tool.name}" failed in ${executionTimeMs}ms: ${errorMsg}`);

			if (this.streamBuffer.hasCallback()) {
				this.streamBuffer.write(`\n\n❌ **${tool.name} failed:** ${errorMsg}\n\n`);
			}

			return `❌ Tool "${tool.name}" failed: ${errorMsg}`;
		}
	}

	// ============================================
	// System Prompt
	// ============================================

	/**
	 * Build the main agent's system prompt with source summaries, direct tool descriptions,
	 * and workflow component descriptions.
	 */
	private async buildSystemPrompt(
		summaries: SourceSummary[],
		directTools: ExternalTool[],
		workflows: WorkflowDescriptor[],
		conversationHistory?: string
	): Promise<string | any[]> {
		const summariesText = formatSummariesForPrompt(summaries);

		// Tool call budget: reserve 2 iterations for the final analysis response
		const maxSourceCalls = Math.max(2, this.config.maxIterations - 2);

		// Build direct tools section for system prompt
		let directToolsText = '';
		if (directTools.length > 0) {
			directToolsText = directTools.map((t, idx) => {
				const params = (t as any).params || {};
				const paramList = Object.entries(params).map(([k, v]) => `    - ${k}: ${v}`).join('\n');
				return `${idx + 1}. **${t.name}** (tool: ${t.id})\n  ${t.description || 'No description'}${paramList ? '\n  Parameters:\n' + paramList : ''}`;
			}).join('\n\n');
		}

		// Build workflows section for system prompt
		let workflowsText = '';
		if (workflows.length > 0) {
			workflowsText = workflows.map((w, idx) => {
				const propLines = Object.entries(w.propsSchema || {})
					.map(([k, v]) => `    - ${k}: ${v}`)
					.join('\n');
				return [
					`${idx + 1}. **${w.name}** (tool: ${w.id})`,
					`  ${w.description}`,
					`  When to use: ${w.whenToUse}`,
					propLines ? `  Props:\n${propLines}` : '',
				]
					.filter(Boolean)
					.join('\n');
			}).join('\n\n');
		} else {
			workflowsText = 'No workflow components registered for this project.';
		}

		const prompts = await promptLoader.loadPrompts('agent-main', {
			SOURCE_SUMMARIES: summariesText,
			DIRECT_TOOLS: directToolsText,
			WORKFLOW_COMPONENTS: workflowsText,
			MAX_ROWS: String(MAIN_AGENT_COMPLETE_ROWS),
			MAX_ROWS_RENDERED: String(MAX_ROWS_RENDERED),
			MAX_ROWS_FETCHED: String(MAX_ROWS_FETCHED),
			MAX_SOURCE_CALLS: String(maxSourceCalls),
			GLOBAL_KNOWLEDGE_BASE: this.config.globalKnowledgeBase || 'No global knowledge base available.',
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
			KNOWLEDGE_BASE_CONTEXT: this.config.knowledgeBaseContext || 'No additional knowledge base context available.',
			CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
		});
		return prompts.system;
	}

	// ============================================
	// Tool Definitions
	// ============================================

	/**
	 * Build tool definitions for source tools — summary-only descriptions.
	 * The full schema is inside the SourceAgent which runs independently.
	 */
	private buildSourceToolDefinitions(summaries: SourceSummary[]): any[] {
		return summaries.map(summary => {
			const totalRows = summary.entityDetails.reduce((sum, e) => sum + (e.rowCount || 0), 0);
			const rowInfo = totalRows > 0 ? ` (~${totalRows.toLocaleString()} total rows)` : '';

			const entitiesList = summary.entityDetails.length > 0
				? summary.entityDetails.map(e => {
					const cols = e.columns.length > 0 ? ` [${e.columns.join(', ')}]` : '';
					return `${e.name}${cols}`;
				}).join('; ')
				: 'no entities listed';

			return {
				name: summary.toolId,
				description: `Query "${summary.name}" (${summary.type})${rowInfo}. ${summary.description}. Contains: ${entitiesList}.`,
				input_schema: {
					type: 'object',
					properties: {
						intent: {
							type: 'string',
							description: 'Describe what data you need from this source in natural language. Be specific about fields, filters, aggregations, and limits.',
						},
						aggregation: {
							type: 'string',
							enum: ['raw', 'pre-aggregate', 'summary'],
							description: 'How to return data. "pre-aggregate": use GROUP BY/SUM/COUNT for totals. "summary": high-level metrics. "raw": individual records.',
						},
					},
					required: ['intent'],
				},
			};
		});
	}

	/**
	 * Build tool definitions for direct tools — expose their actual params.
	 * These are called directly by the main agent LLM, no SourceAgent.
	 */
	private buildDirectToolDefinitions(directTools: ExternalTool[]): any[] {
		return directTools.map(tool => {
			const properties: Record<string, any> = {};
			const required: string[] = [];

			const toolParams: Record<string, any> = (tool as any).params || {};
			Object.entries(toolParams).forEach(([key, typeOrValue]) => {
				const valueStr = String(typeOrValue).toLowerCase();

				// Extract type
				let schemaType = 'string';
				const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
				if (typeMatch) {
					schemaType = typeMatch[1];
				}

				// Check if optional
				const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');

				const description = typeof typeOrValue === 'string' ? typeOrValue : `Parameter: ${key}`;

				if (schemaType === 'array') {
					properties[key] = { type: 'array', items: { type: 'string' }, description };
				} else if (schemaType === 'object') {
					properties[key] = { type: 'object', description };
				} else {
					properties[key] = { type: schemaType, description };
				}

				if (!isOptional) {
					required.push(key);
				}
			});

			return {
				name: tool.id,
				description: tool.description || `Call ${tool.name}`,
				input_schema: {
					type: 'object',
					properties,
					required: required.length > 0 ? required : undefined,
				},
			};
		});
	}

	// ============================================
	// Workflow Handling
	// ============================================

	/**
	 * Capture a workflow selection. We do NOT execute anything — the LLM has
	 * already extracted the props it wants the workflow rendered with. We
	 * record the selection (via the capture callback) and return a short
	 * acknowledgement so the LLM ends its turn cleanly without writing
	 * analysis text or calling more tools.
	 */
	private async handleWorkflow(
		workflow: WorkflowDescriptor,
		toolInput: any,
		capture: (selected: SelectedWorkflow) => void,
	): Promise<string> {
		const props = { ...(workflow.defaultProps || {}), ...(toolInput || {}) };
		logger.info(
			`[MainAgent] Workflow selected: "${workflow.name}" | props: ${JSON.stringify(props).substring(0, 200)}`,
		);

		if (this.streamBuffer.hasCallback()) {
			this.streamBuffer.write(`\n\n🧭 **Launching ${workflow.name} workflow...**\n\n`);
			await streamDelay();
		}

		capture({ name: workflow.name, props });

		// Returning this string tells the main agent its job is done — the UI
		// will render the workflow component, no analysis text needed.
		return `✅ Workflow "${workflow.name}" selected. The UI will render now — do NOT write analysis text or call any other tools. End your turn.`;
	}

	/**
	 * Build LLM tool definitions for workflow components. The workflow's
	 * propsSchema becomes the tool's input_schema so the LLM extracts props
	 * directly from the prompt — same mechanic as direct tools.
	 */
	private buildWorkflowToolDefinitions(workflows: WorkflowDescriptor[]): any[] {
		return workflows.map(workflow => {
			const properties: Record<string, any> = {};
			const required: string[] = [];

			Object.entries(workflow.propsSchema || {}).forEach(([key, typeOrValue]) => {
				const valueStr = String(typeOrValue).toLowerCase();

				let schemaType = 'string';
				const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
				if (typeMatch) {
					schemaType = typeMatch[1];
				}

				const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');
				const description = typeof typeOrValue === 'string' ? typeOrValue : `Prop: ${key}`;

				if (schemaType === 'array') {
					properties[key] = { type: 'array', items: {}, description };
				} else if (schemaType === 'object') {
					properties[key] = { type: 'object', description };
				} else {
					properties[key] = { type: schemaType, description };
				}

				if (!isOptional) required.push(key);
			});

			return {
				name: workflow.id,
				description: `[WORKFLOW] ${workflow.description} — When to use: ${workflow.whenToUse}`,
				input_schema: {
					type: 'object',
					properties,
					required: required.length > 0 ? required : undefined,
				},
			};
		});
	}

	// ============================================
	// Format Result for Main Agent
	// ============================================

	/**
	 * Format a source agent's result as a clean string for the main agent LLM.
	 */
	private formatResultForMainAgent(result: SourceAgentResult): string {
		if (!result.success) {
			return `Data source "${result.sourceName}" could not fulfill the request: ${result.error}. Try rephrasing your intent or querying a different source.`;
		}

		const { metadata } = result;

		let output = `## Data from "${result.sourceName}"\n`;
		output += `Rows returned: ${metadata.rowsReturned}`;

		if (metadata.isLimited) {
			output += ` (LIMITED — ${metadata.totalRowsMatched} total matched, only ${metadata.rowsReturned} returned)`;
		}
		output += `\nExecution time: ${metadata.executionTimeMs}ms\n\n`;

		// Pair EVERY successful query's SQL with the rows IT returned, under one
		// `### Query N` header each. Two reasons this must be per-query:
		//  1. The main agent re-authors SQL at write_script time, so it needs the
		//     proven SQL of every query verbatim — not just the last — or it
		//     silently drops filters/scope (e.g. per-district benchmark coupling).
		//  2. A follow-up query overwrites `result.data` (source-agent keeps only
		//     the last query's rows). Reading each query's own `_sampleData` here
		//     means an earlier query's rows are never hidden by a later one, and
		//     SQL ↔ data are never mixed up across queries.
		const successfulTools = (result.allExecutedTools && result.allExecutedTools.length > 0)
			? result.allExecutedTools
			: (result.executedTool ? [result.executedTool] : []);
		const queries = successfulTools
			.map(t => ({
				sql: (t?.params?.sql || t?.params?.query) as string | undefined,
				rows: (t?.result?._mainAgentRows ?? t?.result?._sampleData ?? []) as Record<string, any>[],
				summary: t?.result?._summary,
				total: t?.result?._totalRecords as number | undefined,
				shown: t?.result?._recordsShown as number | undefined,
			}))
			.filter(q => Boolean(q.sql));

		if (queries.length === 0) {
			output += 'No data returned.';
			return output;
		}

		// Field-level truncation so one wide text cell can't bloat the context.
		const truncRows = (rowsArr: Record<string, any>[]) => rowsArr.map(row => {
			const out: Record<string, any> = {};
			for (const [key, value] of Object.entries(row)) {
				// Surrogate-aware truncation — splitting a UTF-16 pair here would
				// emit a lone surrogate and break the JSON request body.
				out[key] = (typeof value === 'string' && value.length > 200)
					? safeTruncate(value, 200) + '...'
					: value;
			}
			return out;
		});
		const jsonBlock = (obj: any) => {
			try { return '```json\n' + JSON.stringify(obj, null, 2) + '\n```\n'; }
			catch { return '```\n[Data could not be serialized]\n```\n'; }
		};

		const scopeWarning = `PRESERVE every filter and scope — brand, product, date range, AND geography/segment (\`State\`/\`Region\`/\`District\`). Do NOT broaden, drop, or "simplify" any predicate.`;
		output += queries.length === 1
			? `### The SQL below and its result — copy the SQL VERBATIM into \`write_script\` (inside \`ctx.query(...)\`)\n${scopeWarning}\n\n`
			: `### ${queries.length} successful queries — each block pairs ONE query's SQL with ITS OWN result. Do NOT mix a query's SQL with another query's data.\n${scopeWarning} The script re-runs them, so you can include more than one \`ctx.query(...)\`.\n\n`;

		queries.forEach((q, i) => {
			const total = q.total ?? q.rows.length;
			// A query that returned exactly the fetch cap was almost certainly
			// TRUNCATED — and if it was ORDER BY'd, the rows are one slice of the
			// data (e.g. all from the first district), NOT a representative sample.
			// Flag it loudly so the agent re-queries with aggregation instead of
			// concluding a group is absent / reading totals from a partial slice.
			const capped = (q.shown ?? q.rows.length) >= MAX_ROWS_FETCHED;
			output += queries.length === 1
				? `**SQL:**\n`
				: `### Query ${i + 1} — ${capped ? `${MAX_ROWS_FETCHED}+ (TRUNCATED)` : `${total}`} rows\n**SQL for Query ${i + 1}:**\n`;
			output += '```sql\n' + String(q.sql) + '\n```\n';
			if (capped) {
				output += `> ⚠️ **TRUNCATED, NON-REPRESENTATIVE SAMPLE.** This query hit the ${MAX_ROWS_FETCHED}-row exploration cap, so it returned only the FIRST ${MAX_ROWS_FETCHED} rows — and because it is \`ORDER BY\`'d, those rows are likely ALL from one group (e.g. one District/category), not a cross-section. Do NOT conclude any group "has no data" or read any count/total/min/max from this. To reason over all rows, re-query with **aggregation** (\`GROUP BY\` the dimension, e.g. \`SELECT District, COUNT(*) ... GROUP BY District\`) or compute the metric in SQL — never from this slice.\n`;
			}

			if (capped) {
				// TRUNCATED raw slice: the summary was computed over only the first
				// MAX_ROWS_FETCHED rows (an ORDER BY'd, non-representative slice), NOT
				// the full match. Present it as PARTIAL and non-authoritative so it
				// can't contradict the capped warning above ("do NOT read totals from
				// this"). The agent must re-query with aggregation for real numbers.
				const samples = truncRows(q.rows.slice(0, 3));
				output += `**Partial stats over the truncated ${q.shown ?? q.rows.length}-row slice ONLY (NOT the full result) — do NOT read any total, min/max, ranking, or "all/none" from these. Re-query with aggregation (GROUP BY) to get real numbers:**\n`;
				output += jsonBlock(q.summary ?? { note: 'truncated slice — re-query with aggregation' });
				output += `**Sample rows (first ${samples.length} — row SHAPE only, NOT representative):**\n`;
				output += jsonBlock(samples);
			} else if (total <= MAIN_AGENT_COMPLETE_ROWS) {
				// Small AND un-truncated → send it COMPLETE so the agent never
				// generalizes from a biased head-slice (dimension/lookup tables hit this).
				const rows = truncRows(q.rows);
				output += `**All ${rows.length} rows (COMPLETE result — this is the entire result set, nothing hidden):**\n`;
				output += jsonBlock(rows);
			} else {
				// Large but COMPLETE (e.g. an aggregation returning >N grouped rows) →
				// an authoritative BOUNDED summary over ALL rows: per-group rollups,
				// ranges, and the LABEL-carrying `extremes`/`topK`/`bottomK`/`ordering`
				// facts. The full data is NOT sent — it reaches the UI via the script's
				// queryId, never the LLM context.
				const samples = truncRows(q.rows.slice(0, 3));
				output += `**Result summary — computed over ALL ${total} rows. Use THIS for every count, range, total, and "all/none". For any highest/lowest/peak/trough name it via \`extremes\` and \`ordering\` (they carry the LABEL row); for "top/bottom N" use \`topK\`/\`bottomK\`. NEVER guess which row holds an extreme, and do NOT infer numbers from the samples:**\n`;
				output += jsonBlock(q.summary ?? { totalRows: total });
				output += `**Sample rows (first ${samples.length} of ${total} — to show row SHAPE only, NOT representative of the distribution):**\n`;
				output += jsonBlock(samples);
			}
			output += '\n';
		});

		return output;
	}

	/**
	 * Get source summaries (for external inspection/debugging).
	 */
	getSourceSummaries(): SourceSummary[] {
		return buildSourceSummaries(this.externalTools);
	}
}


/**
 * Best-effort extraction of table names from a set of SQL statements.
 * Matches FROM/JOIN references, including schema-qualified names.
 */
function extractTablesFromSQL(sqls: string[]): string[] {
	const tables = new Set<string>();
	const pattern = /(?:FROM|JOIN)\s+"?([a-zA-Z_][\w.]*)"?/gi;
	for (const sql of sqls) {
		if (!sql) continue;
		let match: RegExpExecArray | null;
		while ((match = pattern.exec(sql)) !== null) {
			if (match[1]) tables.add(match[1]);
		}
	}
	return Array.from(tables);
}
